import { TextInput } from './TextInput';
import { RadioGroup } from './RadioGroup';
import { LABEL_TEXT } from './tokens';
import { formatRut, type TipoDocumento } from '../../utils/rutValidator';

interface DocumentoInputProps {
  readonly tipo: TipoDocumento;
  readonly onTipoChange: (tipo: TipoDocumento) => void;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
}

const TIPO_OPTIONS = [
  { value: 'rut', label: 'RUT' },
  { value: 'pasaporte', label: 'Pasaporte' },
] as const;

/**
 * Campo de documento de identidad: selector RUT/Pasaporte + input.
 * - RUT: formatea en vivo (puntos y guión).
 * - Pasaporte: texto sin formateo.
 * La validación la decide el formulario (vía `validarDocumento`), que pasa el
 * resultado por la prop `error`. Así cada formulario controla el momento de
 * validar (en vivo o al enviar) sin duplicar la lógica del campo.
 */
export const DocumentoInput: React.FC<DocumentoInputProps> = ({
  tipo,
  onTipoChange,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
}) => {
  // RUT: formatea (puntos y guión). Pasaporte: solo letras y números en mayúscula, máx 20.
  const handleValue = (v: string) =>
    onChange(tipo === 'rut' ? formatRut(v) : v.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 20));

  const handleTipo = (t: string) => {
    const next = t as TipoDocumento;
    onTipoChange(next);
    if (next === 'rut') onChange(formatRut(value));
  };

  const placeholder = tipo === 'rut' ? '12.345.678-9' : 'Ej: AB1234567';

  return (
    <div className="space-y-2">
      <span className={`block ${LABEL_TEXT}`}>
        Documento de identidad
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      <RadioGroup
        options={TIPO_OPTIONS}
        value={tipo}
        onChange={handleTipo}
        orientation="horizontal"
        disabled={disabled}
        name="tipo-documento"
      />
      <TextInput
        value={value}
        onChange={handleValue}
        error={error}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  );
};
